import { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  PlanCreateInput,
  PlanDto,
  PlanUpdateInput,
} from '@cms/models';
import { saasApi } from '../../../lib/api/saas.api';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Toggle } from '@/components/ui/toggle';
import { CommonModal } from '@/components/common/CommonModal';

const BILLING_OPTIONS = [
  { value: 'day', label: 'Daily' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'yearly', label: 'Yearly' },
] as const;

type PlanFormData = {
  name: string;
  code: string;
  description: string;
  billingPeriod: string;
  maxUsers: number | undefined;
  status: string;
};

function nameToCode(name: string): string {
  return name
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-_]/g, '');
}

const inputClass =
  'mt-1 block w-full h-9 px-3 py-1.5 text-sm bg-background border border-border rounded-md focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-colors';

const textareaClass =
  'mt-1 block w-full px-3 py-2 text-sm bg-background border border-border rounded-md focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-colors resize-y';

export type PlanFormModalMode = 'create' | 'view' | 'edit';

interface PlanFormModalProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly mode: PlanFormModalMode;
  /** null when mode is 'create' */
  readonly plan: PlanDto | null;
  readonly onSuccess?: () => void;
}

export function PlanFormModal({
  open,
  onClose,
  mode,
  plan,
  onSuccess,
}: PlanFormModalProps) {
  const queryClient = useQueryClient();
  const isCreate = mode === 'create';
  const isView = mode === 'view';

  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm<PlanFormData>({
    defaultValues: {
      code: '',
      name: '',
      description: '',
      billingPeriod: 'monthly',
      maxUsers: undefined,
      status: 'active',
    },
  });

  const nameValue = watch('name');

  useEffect(() => {
    if (plan) {
      reset({
        name: plan.name ?? '',
        code: plan.code ?? '',
        description: plan.description ?? '',
        billingPeriod: (plan.billingPeriod ?? 'monthly') as string,
        maxUsers: plan.maxUsers ?? undefined,
        status: (plan.status ?? 'active') as string,
      });
    } else if (isCreate) {
      reset({
        code: '',
        name: '',
        description: '',
        billingPeriod: 'monthly',
        maxUsers: undefined,
        status: 'active',
      });
    }
  }, [plan, isCreate, reset]);

  useEffect(() => {
    if (isCreate && nameValue !== undefined) {
      setValue('code', nameToCode(nameValue ?? ''));
    }
  }, [isCreate, nameValue, setValue]);

  const createMutation = useMutation({
    mutationFn: async (data: PlanFormData) => {
      const code = data.code?.trim() || nameToCode(data.name ?? '');
      const status = data.status === 'inactive' ? 'inactive' : 'active';
      const payload: PlanCreateInput = {
        code: code || 'plan',
        name: data.name,
        description: data.description || undefined,
        billingPeriod: (data.billingPeriod as 'day' | 'monthly' | 'yearly') || 'monthly',
        status: status as 'active' | 'inactive',
      };
      if (typeof data.maxUsers === 'number' && !Number.isNaN(data.maxUsers)) {
        payload.maxUsers = data.maxUsers;
      }
      return saasApi.createPlan(payload);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['saas-plans'] });
      onSuccess?.();
      reset();
      onClose();
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: PlanFormData) => {
      if (!plan?.id) return Promise.reject(new Error('No plan'));
      const payload: PlanUpdateInput = {
        name: data.name,
        description: data.description || undefined,
        billingPeriod: (data.billingPeriod as 'day' | 'monthly' | 'yearly') || 'monthly',
        maxUsers: data.maxUsers,
        status: (data.status as 'active' | 'inactive') ?? 'active',
      };
      return saasApi.updatePlan(String(plan.id), payload);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['saas-plans'] });
      onSuccess?.();
      onClose();
    },
  });

  const onSubmit = async (data: PlanFormData) => {
    if (isView) return;
    try {
      if (isCreate) {
        await createMutation.mutateAsync(data);
      } else {
        await updateMutation.mutateAsync(data);
      }
    } catch (error: unknown) {
      const message =
        error instanceof Error
          ? error.message
          : isCreate
            ? 'Failed to create plan'
            : 'Failed to update plan';
      alert(message);
    }
  };

  const title =
    mode === 'create'
      ? 'Create Plan'
      : mode === 'view'
        ? 'Plan details'
        : 'Edit plan';

  const readOnly = isView;

  return (
    <CommonModal
      open={open}
      onClose={onClose}
      title={title}
      titleId="plan-form-modal-title"
      panelClassName="max-w-2xl max-h-[90vh] overflow-y-auto"
    >
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="space-y-4"
        noValidate
      >
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[180px]">
              <label
                htmlFor="plan-form-name"
                className="block text-sm font-medium text-foreground"
              >
                Name *
              </label>
              <input
                id="plan-form-name"
                type="text"
                {...register('name', { required: 'Name is required' })}
                disabled={readOnly}
                className={cn(
                  inputClass,
                  readOnly && 'cursor-default bg-muted/50'
                )}
              />
              {!readOnly && errors.name && (
                <p className="mt-1 text-sm text-destructive">
                  {errors.name.message}
                </p>
              )}
            </div>
            {readOnly ? (
              <div>
                <span className="block text-sm font-medium text-muted-foreground mb-1">
                  Status
                </span>
                <span className="inline-flex items-center rounded-md border border-border px-2.5 py-1 text-sm capitalize">
                  {watch('status') || '—'}
                </span>
              </div>
            ) : (
              <Controller
                name="status"
                control={control}
                render={({ field }) => (
                  <Toggle
                    id="plan-form-status"
                    label="Status"
                    checked={field.value === 'active'}
                    onCheckedChange={checked =>
                      field.onChange(checked ? 'active' : 'inactive')
                    }
                  />
                )}
              />
            )}
          </div>

          <div>
            <label
              htmlFor="plan-form-code"
              className="block text-sm font-medium text-foreground"
            >
              Code {isCreate ? '(auto from name)' : ''}
            </label>
            <input
              id="plan-form-code"
              type="text"
              readOnly
              {...register('code')}
              className={cn(inputClass, 'cursor-default bg-muted/50')}
            />
          </div>

          <div>
            <label
              htmlFor="plan-form-description"
              className="block text-sm font-medium text-foreground"
            >
              Description
            </label>
            <textarea
              id="plan-form-description"
              rows={3}
              {...register('description')}
              disabled={readOnly}
              className={cn(
                textareaClass,
                readOnly && 'cursor-default bg-muted/50'
              )}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label
                htmlFor="plan-form-billingPeriod"
                className="block text-sm font-medium text-foreground"
              >
                Billing period
              </label>
              <select
                id="plan-form-billingPeriod"
                {...register('billingPeriod')}
                disabled={readOnly}
                className={cn(
                  inputClass,
                  readOnly && 'cursor-default bg-muted/50'
                )}
              >
                {BILLING_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label
                htmlFor="plan-form-maxUsers"
                className="block text-sm font-medium text-foreground"
              >
                Max users
              </label>
              <input
                id="plan-form-maxUsers"
                type="number"
                min={1}
                {...register('maxUsers', { valueAsNumber: true })}
                disabled={readOnly}
                className={cn(
                  inputClass,
                  readOnly && 'cursor-default bg-muted/50'
                )}
              />
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t border-border">
            {readOnly ? (
              <Button type="button" variant="outline" onClick={onClose}>
                Close
              </Button>
            ) : (
              <>
                <Button type="button" variant="outline" onClick={onClose}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={
                    isCreate
                      ? createMutation.isPending
                      : updateMutation.isPending
                  }
                >
                  {isCreate
                    ? createMutation.isPending
                      ? 'Creating…'
                      : 'Create'
                    : updateMutation.isPending
                      ? 'Saving…'
                      : 'Save'}
                </Button>
              </>
            )}
          </div>
      </form>
    </CommonModal>
  );
}
